import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PublicNav, PublicFooter } from "../../components/public/PublicNav";
import { ReleasePosterCard } from "../../components/public/ReleasePosterCard";
import { AdSlot } from "../../components/public/AdSlot";
import { useBulkVotes } from "../../hooks/useBulkVotes";
import { useSeo } from "../../hooks/useSeo";
import { Search, ArrowRight } from "lucide-react";

const SEO_PRESETS = {
  "Film-released": {
    title: "Déjà dans les salles — Films sortis à voter | Rouge Première",
    description: "Films récemment sortis au cinéma en France. Votez pour vos coups de cœur et découvrez le Top 3 du mois sur Rouge Première.",
  },
  "Série-released": {
    title: "Déjà sur vos écrans — Séries à voter | Rouge Première",
    description: "Les séries récemment sorties en streaming (Netflix, Disney+, Prime, Apple TV+, Canal+, Max, Arte). Votez pour vos préférées sur Rouge Première.",
  },
  default: {
    title: "Toutes les sorties cinéma & séries | Rouge Première",
    description: "Le calendrier complet des sorties cinéma et séries en France : filtres par plateforme, pays et type. Rouge Première l'agenda cinéma & séries.",
  },
};

export const PublicReleases = ({ preset }) => {
  // preset can be: { kind: 'Film'|'Série', released_only: true } to lock filters
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState(preset?.kind || "all");
  const [platform, setPlatform] = useState("all");
  const [country, setCountry] = useState("all");
  const [genre, setGenre] = useState("all");
  const [catalogNew, setCatalogNew] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(30);
  const releasedMode = preset?.released_only === true;
  const catalogNewEligible = releasedMode && preset?.kind === "Série";

  const seoKey = releasedMode && preset?.kind ? `${preset.kind}-released` : "default";
  useSeo(SEO_PRESETS[seoKey] || SEO_PRESETS.default);

  useEffect(() => {
    api.get("/config").then((r) => setPlatforms(r.data.target_platforms));
    // Genre & country lists are scoped to the current kind (Film/Série) so
    // the dropdown only surfaces genres actually present for that type.
    const params = preset?.kind ? { kind: preset.kind } : {};
    api.get("/releases/countries", { params }).then((r) => setCountries(r.data));
    api.get("/releases/genres", { params }).then((r) => setGenres(r.data));
  }, [preset?.kind]);

  // Debounce the search query so we don't spam the backend on every
  // keystroke. Empty query resets immediately for snappier UX.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setDebouncedQ("");
      return;
    }
    const t = setTimeout(() => setDebouncedQ(trimmed), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (kind !== "all") params.kind = kind;
    if (platform !== "all") params.platform = platform;
    if (country !== "all") params.country = country;
    if (genre !== "all") params.genre = genre;
    if (debouncedQ) {
      // When the user is searching, delegate to the backend so we can
      // find titles outside the current page's temporal window (upcoming
      // vs released) and beyond the 500-item fetch cap. Drop temporal
      // filters here — the backend disables `upcoming_only` when `q` is
      // present and we intentionally skip `released_only` so a search
      // for e.g. "Bill Maher" surfaces on `/sorties` too.
      params.q = debouncedQ;
    } else {
      if (releasedMode) params.released_only = true;
      if (catalogNewEligible && catalogNew) params.catalog_new_only = true;
    }
    api.get("/releases", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [kind, platform, country, genre, releasedMode, catalogNewEligible, catalogNew, debouncedQ]);

  const filtered = useMemo(() => {
    // Backend already filtered when `debouncedQ` is active, but we keep
    // a local filter so keystrokes between debounce ticks feel instant
    // on the already-loaded set.
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => {
      const t = (i.title || "").toLowerCase();
      const ot = (i.original_title || "").toLowerCase();
      return t.includes(s) || ot.includes(s);
    });
  }, [items, q]);

  const shown = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const { statsById, mineById } = useBulkVotes(shown);

  // reset visible count on filter/search change
  useEffect(() => { setVisibleCount(30); }, [kind, platform, country, genre, q, catalogNew]);

  return (
    <div>
      <PublicNav />
      <section className="max-w-[1300px] mx-auto px-5 md:px-10 pt-12 md:pt-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">
          {releasedMode ? "Vos réactions" : "L'agenda"}
        </div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">
          {releasedMode && preset?.kind === "Film" ? (
            <>Déjà dans <span className="editorial-underline">les salles</span></>
          ) : releasedMode && preset?.kind === "Série" ? (
            <>Déjà sur <span className="editorial-underline">vos écrans</span></>
          ) : (
            <>Toutes les <span className="editorial-underline">sorties</span></>
          )}
        </h1>
        <p className="mt-3 text-neutral-600 max-w-2xl">
          {releasedMode
            ? catalogNewEligible && catalogNew
              ? `${items.length} série${items.length > 1 ? "s" : ""} récemment ajoutée${items.length > 1 ? "s" : ""} au catalogue France (30 derniers jours). Votez pour vos préférées.`
              : `${items.length} ${preset?.kind === "Film" ? "film" : "série"}${items.length > 1 ? "s" : ""} ${preset?.kind === "Film" ? "récemment en salle" : "récemment disponibles"}. Votez pour vos préférés.`
            : `${items.length} film${items.length > 1 ? "s" : ""} et séries à découvrir sur les 60 prochains jours en France.`}
        </p>
      </section>

      <section className="max-w-[1300px] mx-auto px-5 md:px-10 mt-10 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-[var(--line)]">
          <div className="flex items-center gap-2 flex-wrap">
            {!preset?.kind && [
              ["all", "Tout"],
              ["Film", "Films"],
              ["Série", "Séries"],
            ].map(([k, l]) => (
              <button
                key={k}
                data-testid={`pub-filter-${k.toLowerCase()}`}
                onClick={() => setKind(k)}
                className={`text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${
                  kind === k
                    ? "bg-[var(--red)] text-white border-[var(--red)]"
                    : "bg-white text-neutral-700 border-[var(--line-strong)] hover:border-black"
                }`}
              >
                {l}
              </button>
            ))}
            <select
              data-testid="pub-filter-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="ml-2 text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)]"
            >
              <option value="all">Toutes plateformes</option>
              {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              data-testid="pub-filter-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)]"
            >
              <option value="all">Tous pays</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              data-testid="pub-filter-genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-full bg-white border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)]"
            >
              <option value="all">Tous genres</option>
              {genres.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {catalogNewEligible && (
              <label
                data-testid="pub-filter-catalog-new"
                className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border cursor-pointer select-none transition-colors ${
                  catalogNew
                    ? "bg-[var(--red)] text-white border-[var(--red)]"
                    : "bg-white text-neutral-700 border-[var(--line-strong)] hover:border-black"
                }`}
              >
                <input
                  type="checkbox"
                  checked={catalogNew}
                  onChange={(e) => setCatalogNew(e.target.checked)}
                  className="sr-only"
                />
                Nouveautés catalogue
              </label>
            )}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              data-testid="pub-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un titre…"
              className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-[var(--line-strong)] text-sm placeholder:text-neutral-400 focus:outline-none focus:border-[var(--red)]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-[1300px] mx-auto px-5 md:px-10">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] rounded-md bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            platform={platform}
            kind={kind}
            country={country}
            genre={genre}
            releasedMode={releasedMode}
            hasSearch={q.trim().length > 0}
          />
        ) : (
          <>
            <div data-testid="pub-releases-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
              {shown.slice(0, 15).map((r) => (
                <ReleasePosterCard
                  key={`${r.type}-${r.tmdb_id}`}
                  r={r}
                  statsById={statsById}
                  mineById={mineById}
                />
              ))}
            </div>
            <AdSlot slot="releases-mid" className="my-12" />
            {shown.length > 15 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                {shown.slice(15).map((r) => (
                  <ReleasePosterCard
                    key={`${r.type}-${r.tmdb_id}`}
                    r={r}
                    statsById={statsById}
                    mineById={mineById}
                  />
                ))}
              </div>
            )}
            {visibleCount < filtered.length && (
              <div className="mt-12 flex justify-center">
                <button
                  data-testid="load-more-btn"
                  onClick={() => setVisibleCount((c) => c + 30)}
                  className="btn-ghost"
                >
                  Voir plus ({filtered.length - visibleCount} restant{filtered.length - visibleCount > 1 ? "s" : ""})
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <PublicFooter />
    </div>
  );
};

/** Contextual empty state — when a filter (platform / kind / country / genre)
 * eliminates all results on the "upcoming" view, suggest the "already
 * released" pages instead, so a user filtering by Max or Arte doesn't
 * assume the platform is unsupported. Data-testid: pub-empty-state. */
const EmptyState = ({ platform, kind, country, genre, releasedMode, hasSearch }) => {
  const hasFilter =
    platform !== "all" || (kind && kind !== "all") || country !== "all" || genre !== "all";
  // If we're on `/sorties` (upcoming) AND at least one filter is active AND
  // the user is NOT searching (search covers all dates already), offer
  // shortcuts to the released pages.
  const shouldSuggestReleased = !releasedMode && hasFilter && !hasSearch;

  if (!shouldSuggestReleased) {
    return (
      <div className="text-center py-24 text-neutral-500 text-sm" data-testid="pub-empty-state">
        Aucune sortie ne correspond à ces filtres.
      </div>
    );
  }

  // Choose which "already released" links to surface based on the current
  // `kind` selector.
  const links = [];
  if (kind === "Film" || kind === "all") {
    links.push({ to: "/deja-dans-les-salles", label: "Déjà dans les salles", sub: "Films sortis récemment" });
  }
  if (kind === "Série" || kind === "all") {
    links.push({ to: "/deja-sur-vos-ecrans", label: "Déjà sur vos écrans", sub: "Séries récemment disponibles" });
  }

  // Human-readable summary of the active filter, e.g. "Max" or "Netflix, Comédie"
  const parts = [];
  if (platform !== "all") parts.push(platform);
  if (kind && kind !== "all") parts.push(kind === "Film" ? "films" : "séries");
  if (country !== "all") parts.push(country);
  if (genre !== "all") parts.push(genre.toLowerCase());
  const filterLabel = parts.join(" · ");

  return (
    <div
      className="max-w-2xl mx-auto text-center py-16 md:py-20"
      data-testid="pub-empty-state"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">
        Aucune sortie à venir
      </div>
      <h2 className="font-display text-2xl md:text-3xl mt-3 leading-tight">
        Pas de nouveauté annoncée<br className="hidden md:inline" />
        {filterLabel && (
          <span className="text-neutral-500 italic block md:inline md:ml-2">
            pour {filterLabel}
          </span>
        )}
      </h2>
      <p className="mt-4 text-neutral-600 text-sm md:text-base leading-relaxed">
        Cette combinaison de filtres n'a rien dans les 60 prochains jours,
        mais son catalogue est déjà accessible plus bas.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="group inline-flex flex-col items-center sm:items-start text-left px-6 py-4 rounded-md border border-[var(--line-strong)] hover:border-[var(--red)] transition-colors"
            data-testid={`pub-empty-cta-${l.to.replace(/^\//, "")}`}
          >
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 group-hover:text-[var(--red)] transition-colors">
              {l.sub}
            </span>
            <span className="font-display text-lg mt-1 inline-flex items-center gap-2">
              {l.label}
              <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </span>
          </Link>
        ))}
      </div>
      <p className="mt-8 text-xs text-neutral-400">
        Astuce : utilisez la barre de recherche pour trouver un titre précis,
        quelle que soit sa date de sortie.
      </p>
    </div>
  );
};
